/** Formatting helpers shared by UT screens. Pure, no React. */

/** "1,244" — en-GB grouping, as the design shows XP. */
export function formatXp(xp: number | null | undefined): string {
  return Math.round(xp ?? 0).toLocaleString('en-GB');
}

/** Win rate is only meaningful after 10 caps (matches PlayerCardBack). */
export const WIN_RATE_MIN_CAPS = 10;

/** "57%" or "Pending" below 10 caps / when unknown. */
export function winRateLabel(winRate: number | null | undefined, caps: number | null | undefined): string {
  if ((caps ?? 0) < WIN_RATE_MIN_CAPS || winRate == null || Number.isNaN(winRate)) return 'Pending';
  return `${Math.round(winRate)}%`;
}

/** "DP" from "Dave P"; up to two letters. */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

/** "+12" / "-3" / "0". */
export function signed(n: number | null | undefined): string {
  const v = Math.round(n ?? 0);
  return v > 0 ? `+${v}` : String(v);
}

export interface RunInput {
  currentWinStreak?: number | null;
  currentUnbeatenStreak?: number | null;
  currentLossStreak?: number | null;
  currentWinlessStreak?: number | null;
}

/**
 * Current result run as the design's short label: "W7" (wins), "U5" (unbeaten,
 * includes draws), "L2" (losses), "D1" (winless without a loss, i.e. draws).
 * A win run beats an unbeaten run of the same length; "—" when there's no run.
 */
export function runLabel(r: RunInput): string {
  const w = r.currentWinStreak ?? 0;
  const u = r.currentUnbeatenStreak ?? 0;
  const l = r.currentLossStreak ?? 0;
  const wl = r.currentWinlessStreak ?? 0;
  if (w > 0 && w >= u) return `W${w}`;
  if (u > 0) return `U${u}`;
  if (l > 0) return `L${l}`;
  if (wl > 0) return `D${wl}`;
  return '—';
}

/** "2h ago", "Yesterday", "8 days ago", or a short date beyond 4 weeks. */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const mins = Math.floor((now.getTime() - then.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 28) return `${days} days ago`;
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
